import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Building2, Plus, X, ChevronRight, MapPin, Briefcase } from "lucide-react";

const STATUS_STYLE = {
  Active: "bg-emerald-100 text-emerald-700",
  Prospect: "bg-amber-100 text-amber-700",
  Inactive: "bg-slate-200 text-slate-600",
};

const Stat = ({ label, value }) => (
  <div className="text-center"><div className="num font-bold text-lg text-ink">{value}</div><div className="text-[10px] text-ink-faint uppercase tracking-wide">{label}</div></div>
);

function AddClientModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", industry: "", location: "", website: "", contact_name: "", contact_email: "", contact_phone: "", account_manager: "", status: "Active", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Company name is required");
    setBusy(true);
    try { const { data } = await api.post("/agency/clients", f); toast.success("Client added"); onCreated(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const field = (k, label, ph) => (
    <div><label className="text-xs font-semibold text-ink-soft">{label}</label>
      <input data-testid={`client-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" /></div>
  );
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lift w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-bold text-ink text-lg">Add Client</div>
          <button onClick={onClose}><X className="h-5 w-5 text-ink-faint" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {field("name", "Company Name *", "ABC Technologies")}
          {field("industry", "Industry", "Technology")}
          {field("location", "Location", "Bengaluru")}
          {field("website", "Website", "abctech.com")}
          {field("contact_name", "Primary Contact", "Anil Rao")}
          {field("contact_email", "Contact Email", "hr@abctech.com")}
          {field("contact_phone", "Contact Phone", "+91 ...")}
          {field("account_manager", "Account Manager", "Rahul Sharma")}
          <div><label className="text-xs font-semibold text-ink-soft">Status</label>
            <select data-testid="client-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
              <option>Active</option><option>Prospect</option><option>Inactive</option>
            </select></div>
        </div>
        <div className="mt-3"><label className="text-xs font-semibold text-ink-soft">Notes</label>
          <textarea data-testid="client-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" /></div>
        <button data-testid="save-client" onClick={save} disabled={busy}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Client"}
        </button>
      </div>
    </div>
  );
}

export default function AgencyClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [modal, setModal] = useState(false);
  const load = () => api.get("/agency/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="agency-clients">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Clients</h1>
          <p className="text-ink-soft text-sm mt-1">Manage the companies you recruit for</p>
        </div>
        <button data-testid="add-client-btn" onClick={() => setModal(true)}
          className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full transition-colors">
          <Plus className="h-4 w-4" /> Add Client
        </button>
      </div>

      {!clients ? <div className="min-h-[30vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
        : clients.length === 0 ? <div className="text-center text-ink-soft py-16">No clients yet. Add your first client.</div>
          : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((c) => (
                <button key={c.id} data-testid={`client-card-${c.id}`} onClick={() => navigate(`/agency/clients/${c.id}`)}
                  className="text-left bg-white rounded-2xl border border-slate-200 shadow-card p-5 hover:shadow-lift transition-all group">
                  <div className="flex items-start justify-between mb-3">
                    <span className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center"><Building2 className="h-5 w-5 text-brand" /></span>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[c.status] || STATUS_STYLE.Active}`}>{c.status}</span>
                  </div>
                  <div className="font-heading font-bold text-ink group-hover:text-brand transition-colors">{c.name}</div>
                  <div className="text-xs text-ink-faint mt-0.5 flex items-center gap-3">
                    <span>{c.industry}</span>
                    {c.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.location}</span>}
                  </div>
                  <div className="text-[11px] text-ink-soft mt-2 inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> {c.open_jobs} open job{c.open_jobs === 1 ? "" : "s"} · AM: {c.account_manager || "—"}</div>
                  <div className="mt-4 grid grid-cols-4 gap-1 rounded-xl bg-slate-50 py-3">
                    <Stat label="Active" value={c.active_candidates} />
                    <Stat label="Recomm." value={c.recommended} />
                    <Stat label="Intvw" value={c.interviews} />
                    <Stat label="Selected" value={c.selected} />
                  </div>
                  <div className="mt-3 text-xs font-bold text-brand flex items-center justify-end gap-1">View profile <ChevronRight className="h-3.5 w-3.5" /></div>
                </button>
              ))}
            </div>
          )}

      {modal && <AddClientModal onClose={() => setModal(false)} onCreated={(c) => { setModal(false); setClients((p) => [...(p || []), { ...c, active_candidates: 0, recommended: 0, interviews: 0, selected: 0, open_jobs: 0 }]); navigate(`/agency/clients/${c.id}`); }} />}
    </div>
  );
}
